import { Component, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { AuthService, UIOrg } from 'src/app/auth/service/auth.service';
import { WorkforceLayoutService } from './workforce-layout.service';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { ClipboardService } from 'ngx-clipboard';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { ChatSettingsDialogComponent } from './chat-with-insights/chat-settings-dialog/chat-settings-dialog.component';
import { LangGraphInstanceService } from 'src/app/core/services/langgraph-instance.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root-workforce',
    templateUrl: './app.wf.component.html',
    styleUrls: ['./app.wf.component.scss'],
    standalone: false
})
export class WorkforceAppComponent implements OnDestroy {
  @ViewChild(MatSidenav) sidenav: MatSidenav;
  @ViewChild('inspectPopoverInput') inspectPopoverInput: ElementRef<HTMLInputElement>;
  @ViewChild('inspectTrigger', { read: MatMenuTrigger }) inspectMenuTrigger: MatMenuTrigger;

  adminRoutes = [
    {
      path: 'admin/clean',
      name: 'Clean Service',
    },
    {
      path: 'admin/identity',
      name: 'Identity Service',
    },
    {
      path: 'admin/ledger',
      name: 'Ledger Service',
    },
    {
      path: 'admin/moneyball',
      name: 'Moneyball Service',
    },
    {
      path: 'admin/workforce/quickbuilds',
      name: 'Workforce',
    },
    {
      path: 'admin/firehose',
      name: 'Live Firehose',
    },
    {
      path: 'admin/data-deliveries/report-deliveries',
      name: 'Data Deliveries',
    },
    {
      path: 'datawarehouse/stats',
      name: 'Data Warehouse',
    },
    {
      path: 'admin/providers',
      name: 'Providers',
    },
    {
      path: 'admin/streams/dashboard',
      name: 'Onboarding',
    },
    {
      path: 'admin/billing/subscriptions',
      name: 'Billing',
    },
    {
      path: 'admin/grow',
      name: 'Grow',
    },
    {
      path: 'admin/techno-target',
      name: 'Techno Target',
    },
  ];

  isSmallScreen: boolean = false;
  thinSidenavOpened: boolean = true;
  // claude.ai-style rail expansion: icons-only when collapsed, icons + labels
  // when expanded. Expanded by default; the user's choice is persisted in
  // localStorage and restored on future sessions.
  railExpanded: boolean = localStorage.getItem('wfRailExpanded') !== 'false';
  userInitial: string | undefined;
  orgs: UIOrg[] = [];
  $selectedOrg: Observable<UIOrg | undefined>;
  selectedOrg: UIOrg | undefined;
  $isOrgAdmin: Observable<boolean> = of(false);
  $isOrgActive: Observable<boolean> = of(false);
  $orgCapabilities: Observable<String[]> = of([]);
  $showAdmin: Observable<boolean> = of(false);
  quickSearchValue: string = '';
  appVersion: string = environment.appVersion;
  private layoutSubscription: Subscription;
  private selectedOrgSubscription: Subscription;
  private userSubscription: Subscription;
  private showAdminSubscription: Subscription;
  private orgsSubscription: Subscription;

  constructor(
    public authService: AuthService,
    public layoutService: WorkforceLayoutService,
    private darkModeService: DarkModeService,
    private clipboard: ClipboardService,
    private notify: NotificationService,
    private dialog: MatDialog,
    public langGraphInstanceService: LangGraphInstanceService,
    private _hotkeysService: HotkeysService,
    private router: Router
  ) {
    // responsive thin sidenav; logic belongs here b/c it needs to be ready before the component is init'ed
    this.layoutSubscription = this.layoutService.isSmallScreen$.subscribe((isSmall) => {
      this.isSmallScreen = isSmall;
      if (this.isSmallScreen) {
        this.thinSidenavOpened = false;
        if (this.sidenav) {
          this.sidenav.mode = 'over';
          this.sidenav.close();
        }
      } else {
        this.thinSidenavOpened = true;
        if (this.sidenav) {
          this.sidenav.mode = 'side';
          this.sidenav.open();
        }
      }
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    });
  }

  ngOnInit() {
    this.darkModeService.initializeTheme(); // we need to call it here b/c WF doesn't have a DarkModeToggleComponent which calls theme init
    this.userSubscription = this.authService.$getUser.subscribe((user) => {
      this.userInitial = user?.name
        ?.split(' ')
        .slice(0, 2)
        .map((n) => n.charAt(0).toUpperCase())
        .join('');
    });
    this.$isOrgAdmin = this.authService.$isOrgAdmin;
    this.$isOrgActive = this.authService.$isOrgActive;
    this.$orgCapabilities = this.authService.$getOrgCapabilites;
    this.$showAdmin = this.authService.$isAdmin;
    this.showAdminSubscription = this.$showAdmin.subscribe((isAdmin) => {
      if (!isAdmin) return;

      this.registerAdminHotkeys();
    });

    this.orgsSubscription = this.authService.$getOrgs.subscribe((orgs) => {
      this.orgs = orgs;
    });
    this.$selectedOrg = this.authService.$getSelectedOrg;
    this.selectedOrgSubscription = this.$selectedOrg.subscribe((org) => {
      this.selectedOrg = org;
    });
  }

  // close sidenav when clicking on certain nav buttons on mobile
  // so it doesn't stay annoyingly open
  onNavButtonClick() {
    if (this.isSmallScreen && this.sidenav) {
      this.sidenav.close();
    }
  }

  // Opener for the shell's mobile top bar (the rail is an 'over' overlay on
  // small screens and closes after each nav click).
  openRail() {
    this.sidenav?.open();
  }

  toggleThinSidenav() {
    if (this.sidenav) {
      this.sidenav.toggle();
      this.thinSidenavOpened = this.sidenav.opened;
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    }
  }

  toggleRail() {
    this.railExpanded = !this.railExpanded;
    localStorage.setItem('wfRailExpanded', String(this.railExpanded));
    // let charts and grids re-measure once the width transition settles
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
  }

  // Shared layout classes for rail buttons in the two states.
  get railBtnClass(): string {
    return this.railExpanded
      ? 'tw-w-full tw-h-9 tw-justify-start tw-gap-3 tw-px-2.5'
      : 'tw-w-9 tw-h-9 tw-justify-center';
  }

  // Open the chat history drawer (lives in the chat page). Navigate to /chat first
  // if we're elsewhere so the drawer has somewhere to open.
  openChatHistory() {
    const onChatPage = this.router.url.split(/[?#]/)[0] === '/chat';
    if (onChatPage) {
      this.layoutService.requestOpenChatHistory();
    } else {
      // The chat component isn't mounted yet, so flag the request for it to pick up on init.
      this.layoutService.setPendingOpenChatHistory();
      this.router.navigate(['/chat']);
    }
    this.onNavButtonClick();
  }

  openChatSettings() {
    this.dialog.open(ChatSettingsDialogComponent, { width: '420px' });
    this.onNavButtonClick();
  }

  switchOrg(orgId: string) {
    this.authService.setSelectedOrgId(orgId);
    // Re-run the capability-driven landing redirect so org-scoped pages
    // don't keep showing the previous org's data.
    this.router.navigate(['/']);
  }

  copyOrgId(orgId: string, event: Event) {
    event.stopPropagation();
    this.clipboard.copyFromContent(orgId);
    this.notify.success('Copied Org ID to clipboard');
  }

  showIntercom() {
    (window as any)['Intercom']('showNewMessage');
  }

  triggerRefresh() {
    this.authService.refreshSession().subscribe({
      next: () => {
        this.clipboard.copyFromContent(this.authService.getAccessTokenValue);
        this.notify.success('Refreshed and copied to clipboard');
      },
      error: () => {
        this.notify.error('Error');
      },
    });
  }

  // Focus the popover's input as soon as it opens so the user can paste
  // straight away without clicking into the field.
  focusInspectInput(): void {
    setTimeout(() => this.inspectPopoverInput?.nativeElement?.focus(), 0);
  }

  quickSearch(): void {
    const input = this.quickSearchValue?.trim();
    if (!input) {
      return;
    }
    this.quickSearchValue = ''; // Clear the input after navigation
    this.inspectMenuTrigger?.closeMenu(); // Close the collapsed-mode popover if open
    this.onNavButtonClick(); // Close the rail overlay on small screens

    const lower = input.toLowerCase();

    // Org ID -> identity org dashboard
    if (lower.startsWith('o_')) {
      this.router.navigate(['/admin/identity/orgs', input, 'dashboard']);
      return;
    }

    // User ID -> identity users tab with that user selected
    if (lower.startsWith('u_')) {
      this.router.navigate(['/admin/identity'], {
        queryParams: { tab: 'users', userId: input },
      });
      return;
    }

    // Company LinkedIn URL -> DW companies page for that lipid
    const companyUrlMatch = input.match(/linkedin\.com\/company\/([^\/?#]+)/i);
    if (companyUrlMatch) {
      this.router.navigate(['/datawarehouse/companies'], {
        queryParams: { id: companyUrlMatch[1] },
      });
      return;
    }

    // Company ID -> DW companies page
    if (lower.startsWith('ldc-')) {
      this.router.navigate(['/datawarehouse/companies'], { queryParams: { id: input } });
      return;
    }

    // Person LinkedIn URL -> inspect with the lipid
    const personUrlMatch = input.match(/linkedin\.com\/in\/([^\/?#]+)/i);
    if (personUrlMatch) {
      this.router.navigate(['/datawarehouse/inspect'], {
        queryParams: { ref: personUrlMatch[1] },
      });
      return;
    }

    // Person/contact IDs (ldp-, ldlc-) or a naked lipid -> inspect
    this.router.navigate(['/datawarehouse/inspect'], { queryParams: { ref: input } });
  }

  ngOnDestroy() {
    this.layoutSubscription?.unsubscribe();
    this.selectedOrgSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.showAdminSubscription?.unsubscribe();
    this.orgsSubscription?.unsubscribe();
  }

  private registerAdminHotkeys() {
    this._hotkeysService.add([
      new Hotkey(
        'alt+shift+i',
        (): boolean => {
          this.router.navigate(['/datawarehouse/inspect']);
          return true;
        },
        undefined,
        'Go to Inspect Page'
      ),
      new Hotkey(
        'alt+shift+o',
        (): boolean => {
          this.router.navigate(['/admin/identity']);
          return true;
        },
        undefined,
        'Go to Orgs Page'
      ),
      new Hotkey(
        'alt+shift+q',
        (): boolean => {
          this.inspectPersonAtCursor();
          return true;
        },
        undefined,
        'Inspect Person at Cursor'
      ),
      new Hotkey(
        'alt+shift+c',
        (): boolean => {
          const currentValue = localStorage.getItem('showChatCharts');
          localStorage.setItem('showChatCharts', currentValue === 'true' ? 'false' : 'true');
          return true;
        },
        undefined,
        'Toggle Chat Charts'
      ),
    ]);
  }

  private inspectPersonAtCursor() {
    const selection = window.getSelection();
    let selectedText = '';
    if (selection && !selection.isCollapsed) {
      selectedText = selection.toString().trim();
    } else {
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textNode = range.startContainer;

        if (textNode.nodeType === Node.TEXT_NODE) {
          const text = textNode.textContent || '';
          const position = range.startOffset;
          selectedText = this.getWordAtIndex(text, position);
        }
      }
    }
    if (selectedText.startsWith('LDP-')) {
      this.router.navigate(['/datawarehouse/inspect'], {
        queryParams: { ref: selectedText },
      });
    }
  }

  private getWordAtIndex(text: string, index: number): string {
    // Find word boundaries
    const beforeCursor = text.slice(0, index);
    const afterCursor = text.slice(index);

    const wordStartMatch = beforeCursor.match(/\S*$/);
    const wordEndMatch = afterCursor.match(/^\S*/);

    const wordStart = wordStartMatch ? wordStartMatch[0] : '';
    const wordEnd = wordEndMatch ? wordEndMatch[0] : '';

    return wordStart + wordEnd;
  }
}
